import type { DocSection } from '../types'

export const campaignsSection: DocSection = {
  id: 'campagnes',
  title: 'Campagnes (fiches)',
  icon: 'phone-outgoing',
  accent: '#D946EF',
  summary: "Créer et configurer une campagne d'appels : numérateur, horaires, relances, dispositions, listes de numéros.",
  minRole: 'team_admin',
  articles: [
    {
      id: 'principe',
      title: "C'est quoi une campagne ?",
      summary: "Un groupe de numéros qui partagent un projet, un client, une raison d'appeler.",
      icon: 'phone-outgoing',
      blocks: [
        { type: 'p', text: "Une **campagne** (ou « fiche ») regroupe des numéros à appeler qui partagent une même configuration : qui peut les appeler, à quelles heures, avec quel numéro présenté, quelles relances, quelles dispositions." },
        { type: 'list', items: [
          "Une campagne appartient à **une équipe** : seuls ses agents (et ceux explicitement assignés) reçoivent ses numéros.",
          "Elle est **isolée par compte** : aucune fuite entre clients.",
          "Les **numéros appartiennent aux listes** (« fiches de numéros »), pas à la campagne : une même liste peut alimenter **plusieurs campagnes**. Voir [[campagnes/listes|Listes de numéros]].",
          "L'archiver (au lieu de la supprimer) **préserve l'historique** des appels passés ; une campagne archivée peut être **restaurée**.",
        ] },
        { type: 'callout', variant: 'info', title: 'Accès', text: "Un **Superviseur** gère les campagnes de **son équipe**. Un **Administrateur/Propriétaire** gère toutes les campagnes du compte. La **suppression définitive** est réservée à l'Administrateur/Propriétaire." },
        { type: 'seealso', items: [
          { to: 'campagnes/numerateur', label: 'Configurer le numérateur' },
          { to: 'campagnes/cycle-vie', label: 'Cloner, archiver, supprimer' },
          { to: 'campagnes/listes', label: 'Listes de numéros (fiches) & import' },
          { to: 'dialer', label: 'Mode progressif (appels automatiques)' },
        ] },
      ],
    },
    {
      id: 'creer',
      title: 'Créer une campagne',
      summary: 'Les étapes minimales et le statut de la campagne.',
      icon: 'plus',
      blocks: [
        { type: 'steps', items: [
          "Menu **Campagnes** → **Nouvelle campagne**.",
          "Donnez un **nom** et choisissez l'**équipe** propriétaire.",
          "Réglez le **numérateur** (mode d'appel, numéro présenté) — voir [[campagnes/numerateur|Configurer le numérateur]]. Les valeurs par défaut reproduisent le comportement classique (aperçu).",
          "Définissez horaires/relances si besoin, puis enregistrez.",
          "Ajoutez des numéros (import CSV / liste) — voir [[campagnes/listes|Listes de numéros]].",
        ] },
        { type: 'h', text: 'Statut de la campagne' },
        { type: 'table', headers: ['Statut', 'Appelable ?', 'Usage'], rows: [
          ['Brouillon', 'Non', "En préparation"],
          ['**Actif**', '**Oui**', "Seul statut où les numéros sont distribués/appelés"],
          ['En pause', 'Non', "Suspendue temporairement"],
          ['Archivé', 'Non', "Terminée — historique conservé"],
        ] },
        { type: 'callout', variant: 'warning', text: "Une campagne n'est appelable **que** si elle est **Active** ET dans sa plage horaire. Pensez à la passer Active pour démarrer." },
      ],
    },
    {
      id: 'cycle-vie',
      title: 'Cloner, archiver, restaurer, supprimer',
      summary: "Dupliquer une campagne, la mettre de côté, ou la supprimer définitivement.",
      icon: 'copy',
      blocks: [
        { type: 'h', text: 'Cloner une campagne' },
        { type: 'p', text: "Le bouton **Cloner** (icône copie) crée une **copie de la configuration** d'une campagne : numérateur, horaires, relances, script, dispositions, numéros présentés, champs de fiche et assignations." },
        { type: 'list', items: [
          "La copie naît en **brouillon** (non appelable) — relisez-la avant de l'activer.",
          "Elle reçoit le nom « <nom> (copie) » (modifiable).",
          "Les **numéros de la file ne sont PAS copiés** : la copie démarre avec une file vide. Liez-lui des listes au besoin.",
        ] },
        { type: 'h', text: 'Archiver / restaurer' },
        { type: 'p', text: "**Archiver** une campagne la rend non appelable tout en **conservant son historique**. Elle disparaît de la liste des campagnes actives (filtre **Toutes** pour la revoir). Le même bouton la **restaure** ensuite (elle redevient active)." },
        { type: 'h', text: 'Supprimer définitivement' },
        { type: 'callout', variant: 'danger', title: 'Action irréversible (Admin/Propriétaire)', text: "La **suppression définitive** retire la campagne et sa configuration. Pour confirmer, vous devez **retaper le nom exact** de la campagne." },
        { type: 'list', items: [
          "Les **numéros ne sont pas perdus** : ils appartiennent à leur **liste** et sont simplement **détachés** de la campagne (réutilisables dans une autre).",
          "L'**historique d'appels** (journaux, enregistrements) est **conservé**.",
          "Ce qui part : dispositions, champs, numéros présentés et liste noire **propres** à la campagne.",
        ] },
      ],
    },
    {
      id: 'numerateur',
      title: 'Configurer le numérateur',
      summary: 'Mode d\'appel, numéro présenté, temps de sonnerie.',
      icon: 'phone',
      blocks: [
        { type: 'h', text: "Mode d'appel" },
        { type: 'table', headers: ['Mode', 'Qui compose', 'Pour qui'], rows: [
          ['**Aperçu**', "L'agent (il valide avant l'appel)", 'Par défaut. L\'agent voit la fiche puis clique Appeler.'],
          ['**Progressif**', "Le système compose tout seul", "L'agent ne reçoit que les vraies personnes. Voir [[dialer|Mode progressif]]."],
          ['Power / Prédictif', 'Le système (plusieurs appels par agent)', "Évolutions à venir (à activer avec précaution)."],
        ] },
        { type: 'h', text: 'Numéro présenté' },
        { type: 'p', text: "Le numéro affiché chez la personne appelée. Vous pouvez fixer **un numéro unique**, ou **plusieurs numéros** mis en rotation (à tour de rôle, le moins utilisé, aléatoire, ou **régional** = même indicatif que la personne appelée). Laissé vide, le numéro de l'utilisateur, de l'équipe ou du compte est utilisé. Voir [[telephonie/caller-id|Numéro présenté]]." },
        { type: 'h', text: 'Autres réglages' },
        { type: 'kv', items: [
          { k: 'Temps de sonnerie', v: "De 5 à 300 secondes avant d'abandonner un appel sans réponse." },
          { k: 'Opérateur sortant', v: "L'opérateur utilisé pour cette campagne (sinon celui par défaut du compte)." },
          { k: 'Enregistrement', v: "Activer/désactiver l'enregistrement des appels de la campagne." },
          { k: 'Post-appel (wrap-up)', v: "Temps de saisie accordé à l'agent avant le numéro suivant." },
          { k: 'Avertissement client', v: "Confirmation avant d'appeler un numéro marqué « client »." },
        ] },
      ],
    },
    {
      id: 'horaires',
      title: 'Horaires & relances',
      summary: 'Quand appeler, combien de fois, à quel intervalle.',
      icon: 'clock',
      blocks: [
        { type: 'h', text: "Plage d'appel" },
        { type: 'list', items: [
          "**Fuseau horaire** de la campagne, **heure de début / fin**, et **jours autorisés** (lundi→dimanche).",
          "Vide ⇒ **aucune restriction** (appelable 24/7).",
          "**Programmer la campagne** : une fenêtre globale de dates (début/fin) hors de laquelle elle n'est pas appelable.",
        ] },
        { type: 'callout', variant: 'info', text: "Hors plage horaire, les numéros **restent intacts** : ils ne sont simplement pas distribués tant que l'heure n'est pas revenue." },
        { type: 'h', text: 'Relances automatiques' },
        { type: 'kv', items: [
          { k: 'Tentatives max', v: "Nombre d'appels par numéro avant épuisement (défaut 3)." },
          { k: 'Délai de relance', v: "Temps minimum entre deux tentatives sur un même numéro (défaut 60 min)." },
          { k: 'Résultats qui relancent', v: "Les dispositions de type « Échec, appelez plus tard » remettent le numéro en file tant qu'il reste des tentatives." },
        ] },
        { type: 'callout', variant: 'tip', text: "Un numéro récemment appelé reste **en attente** tant que le délai de relance n'est pas écoulé : il n'est pas redistribué avant." },
      ],
    },
    {
      id: 'listes',
      title: 'Listes de numéros (fiches) & import',
      summary: 'Créer une fiche, y importer des numéros (filtres + récap), la relier à des campagnes, la supprimer.',
      icon: 'upload',
      minRole: 'admin',
      blocks: [
        { type: 'p', text: "Les numéros n'appartiennent **pas** à une campagne mais à une **liste** (« fiche de numéros »). C'est la liste qui est la **source** : chaque campagne reliée reçoit sa **propre copie** des numéros à appeler. Une liste se crée **vierge** (puis on la remplit) ou **par import**, et se **relie** à une ou plusieurs campagnes." },

        { type: 'h', text: 'Créer une fiche vierge' },
        { type: 'steps', items: [
          "Menu **Listes de numéros** → bouton **Créer une fiche**.",
          "Donnez-lui un **nom** clair (ex : « Prospects Montréal »).",
          "Optionnel : cochez les **campagnes** à relier dès la création (modifiable plus tard).",
          "Validez : la fiche apparaît **vide** — ajoutez-y des numéros (à la main ou par import CSV).",
        ] },

        { type: 'h', text: 'Ajouter des numéros à une fiche' },
        { type: 'p', text: "Ouvrez la fiche (**Gérer les numéros**), puis au choix :" },
        { type: 'list', items: [
          "**Un par un** : saisissez le numéro (+ nom optionnel) et **Ajouter**. Un numéro déjà présent est signalé (pas de doublon créé).",
          "**Par CSV** : bouton **Importer CSV** → sélectionnez **un ou plusieurs** fichiers. Colonne **phone** requise (alias : telephone, numero) ; colonnes optionnelles name/nom, notes, email, ville…",
        ] },
        { type: 'callout', variant: 'info', title: 'Deux endroits pour importer', text: "L'import **depuis une fiche** (page **Listes de numéros**) ajoute les numéros à **cette** fiche. L'import de la page **Gestion des appels** vise une **campagne** et crée une fiche auto-nommée par fichier. Pour remplir une fiche précise, importez **depuis la fiche**." },

        { type: 'h', text: "Filtres d'ajout & récap (import dans une fiche)" },
        { type: 'p', text: "À l'import dans une fiche, vous activez les **filtres** qui écartent les numéros indésirables :" },
        { type: 'kv', items: [
          { k: 'Doublons de la fiche', v: "Un numéro déjà dans cette fiche n'est jamais ré-ajouté (toujours appliqué)." },
          { k: 'Liste noire', v: "Les numéros blacklistés (compte + campagne) sont ignorés. Voir [[blacklist|Liste noire]]." },
          { k: 'Numéros invalides', v: "Les numéros au mauvais format (non valides) sont écartés." },
          { k: "Doublons d'autres fiches", v: "Un numéro déjà présent dans une **autre** fiche du compte est écarté (dédoublonnage global)." },
        ] },
        { type: 'p', text: "En fin d'import, un **récap** indique les numéros **ajoutés** et combien ont été écartés par catégorie (doublons fiche, doublons autres fiches, liste noire, invalides)." },

        { type: 'h', text: 'Relier une liste à plusieurs campagnes' },
        { type: 'p', text: "Sur la page **Listes de numéros**, chaque liste a un sélecteur **Campagnes liées** : cochez toutes les campagnes qui doivent travailler cette liste. Chaque campagne reçoit les numéros et les appelle **indépendamment**." },
        { type: 'callout', variant: 'tip', title: 'Progression indépendante', text: "Un même numéro relié à 2 campagnes est traité **séparément** dans chacune : l'appeler/qualifier dans la campagne A le laisse **encore à appeler** dans la campagne B." },

        { type: 'h', text: 'Modifier une liste = mise à jour partout' },
        { type: 'list', items: [
          "**Ajouter un numéro** à la liste l'ajoute à **toutes** les campagnes reliées.",
          "**Retirer un numéro** (ou le désactiver) le retire de **toutes** les campagnes reliées.",
          "**Délier** une campagne en retire les numéros (sa progression sur cette liste est perdue ; la liste et ses numéros restent intacts).",
        ] },
        { type: 'callout', variant: 'info', title: 'Désactiver une liste entière', text: "Désactiver une liste (interrupteur **Active**) **retire tous ses numéros des appels** sans les supprimer, dans toutes les campagnes reliées — pratique pour suspendre un lot. Réversible." },

        { type: 'h', text: 'Supprimer une fiche' },
        { type: 'callout', variant: 'danger', title: 'Action irréversible', text: "La **corbeille** sur une fiche la supprime — elle et ses numéros — et les **retire de toutes les campagnes reliées** (plus aucun numéro appelable issu de cette fiche). Une confirmation est demandée." },
        { type: 'list', items: [
          "L'**historique d'appels / enregistrements** est **conservé**.",
          "Pour seulement suspendre une fiche sans la perdre, préférez la **désactiver** (interrupteur Active).",
          "À l'inverse, **supprimer une campagne** ne supprime pas les numéros : ils restent dans leur liste (et les autres campagnes reliées).",
        ] },
      ],
    },
    {
      id: 'avance',
      title: 'Dispositions, champs, assignations',
      summary: 'Personnaliser les résultats, les champs de fiche et qui accède à la campagne.',
      icon: 'settings',
      blocks: [
        { type: 'h', text: 'Dispositions' },
        { type: 'p', text: "Chaque campagne a ses propres **dispositions** (résultats d'appel) : vous pouvez renommer, recolorer, changer le type, en ajouter/retirer. Voir [[dispositions/types|Les types et leurs effets]]." },
        { type: 'h', text: 'Champs personnalisés & formulaires' },
        { type: 'list', items: [
          "**Champs personnalisés** : des champs propres à la campagne que l'agent remplit (et que vous décidez éditables ou non).",
          "**Formulaire de RDV** : affiché quand l'agent qualifie en « RDV ».",
          "**Script** : texte d'appel affiché à l'agent (texte libre ou script réutilisable).",
        ] },
        { type: 'h', text: 'Qui accède à la campagne' },
        { type: 'list', items: [
          "Par défaut : l'**équipe propriétaire**.",
          "**Groupes attribués** : d'autres équipes peuvent accéder à la campagne.",
          "**Utilisateurs assignés** : des agents nommément, même hors de l'équipe.",
        ] },
        { type: 'h', text: 'Contrôles agent sur la file' },
        { type: 'p', text: "Vous pouvez autoriser/interdire finement à l'agent de **passer (skip)**, **réordonner** ou **supprimer** des numéros de sa file. Par défaut, seul le skip est autorisé." },
      ],
    },
  ],
}
